/**
 * Renders a supplemental questionnaire — the carrier's PDF, as a web form.
 *
 * Driven entirely by the definition, so adding a class is adding a JSON file,
 * never a component. That is the whole point: there are 222 commercial classes
 * in the demand report and hand-building 222 forms is not a plan.
 *
 * The `audience` prop is what makes the agent/insured split work. An agent sees
 * only what an agent can answer; the insured, following their link, sees only
 * theirs. Neither is shown a wall of questions that were never theirs to answer,
 * which is the thing that makes people abandon a form.
 */
import { useMemo } from 'react';
import type { Audience, FormAnswers, FormField, SupplementalForm as FormDef } from '@commpass/shared';
import { completeness } from '@commpass/shared';

interface Props {
  form: FormDef;
  answers: FormAnswers;
  onChange: (fieldId: string, value: FormAnswers[string]) => void;
  /** Restrict to the questions this party can answer. Omit to show everything. */
  audience?: Audience;
  readOnly?: boolean;
}

/** A field is visible when its condition is met — or when it has none. */
function isVisible(field: FormField, answers: FormAnswers, audience?: Audience): boolean {
  if (audience && field.audience !== 'either' && field.audience !== audience) return false;
  if (!field.showIf) return true;
  return answers[field.showIf.field] === field.showIf.equals;
}

/**
 * Should this question have the row to itself?
 *
 * Short answers pair up two to a row — a date, a count and a county sitting
 * alone on a 700px row is what made the form read like a spreadsheet. Anything
 * that needs room, or asks a long question, takes the full width, because a
 * two-line label beside a one-line label makes the column edges ragged and the
 * whole thing look accidental.
 *
 * yesno and table set this in CSS as well; both are always full-width.
 */
function takesFullRow(field: FormField): boolean {
  if (field.type === 'textarea' || field.type === 'table' || field.type === 'multiselect') return true;
  if (field.type === 'yesno') return true;
  if (field.label.length > 62) return true;
  // Addresses and descriptions are short-typed but long in practice.
  return /address|description|explain|name of|list |details/i.test(field.label);
}

function Scalar({ field, value, onChange, readOnly }: {
  field: FormField;
  value: FormAnswers[string];
  onChange: (v: FormAnswers[string]) => void;
  readOnly?: boolean;
}) {
  const common = { id: field.id, disabled: readOnly, 'aria-describedby': field.help ? `${field.id}-help` : undefined };

  switch (field.type) {
    case 'yesno':
      // Radios, not a checkbox. A checkbox cannot tell "no" from "not yet
      // answered", and on an application that difference is the whole point.
      return (
        <div className="sf-yesno">
          {[true, false].map((v) => (
            <label key={String(v)} className={value === v ? 'is-on' : ''}>
              <input
                type="radio"
                name={field.id}
                checked={value === v}
                disabled={readOnly}
                onChange={() => onChange(v)}
              />
              {v ? 'Yes' : 'No'}
            </label>
          ))}
        </div>
      );

    case 'textarea':
      return <textarea {...common} rows={3} value={(value as string) ?? ''} onChange={(e) => onChange(e.target.value)} />;

    case 'select':
      return (
        <select {...common} value={(value as string) ?? ''} onChange={(e) => onChange(e.target.value)}>
          <option value="">—</option>
          {(field.options ?? []).map((o) => (
            <option key={o} value={o}>{o}</option>
          ))}
        </select>
      );

    case 'multiselect': {
      const chosen = Array.isArray(value) ? (value as unknown as string[]) : [];
      return (
        <div className="sf-multi">
          {(field.options ?? []).map((o) => (
            <label key={o} className={chosen.includes(o) ? 'is-on' : ''}>
              <input
                type="checkbox"
                checked={chosen.includes(o)}
                disabled={readOnly}
                onChange={() =>
                  onChange((chosen.includes(o) ? chosen.filter((x) => x !== o) : [...chosen, o]) as never)
                }
              />
              {o}
            </label>
          ))}
        </div>
      );
    }

    case 'number':
    case 'currency':
    case 'percent':
      return (
        <div className="sf-adorned">
          {field.type === 'currency' && <span className="sf-adorn">$</span>}
          <input
            {...common}
            type="number"
            inputMode="decimal"
            value={(value as number) ?? ''}
            onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
          />
          {(field.unit || field.type === 'percent') && (
            <span className="sf-adorn is-suffix">{field.unit ?? '%'}</span>
          )}
        </div>
      );

    case 'date':
      return <input {...common} type="date" value={(value as string) ?? ''} onChange={(e) => onChange(e.target.value)} />;

    default:
      return (
        <div className="sf-adorned">
          <input {...common} type="text" value={(value as string) ?? ''} onChange={(e) => onChange(e.target.value)} />
          {field.unit && <span className="sf-adorn is-suffix">{field.unit}</span>}
        </div>
      );
  }
}

/**
 * Tables come in two shapes and both are real.
 *
 * A fixed-row grid (`rowLabels`) is the seven days of the week or three years of
 * loss history — the rows are printed on the form and must all be there. A
 * free-row table is a vehicle schedule, where nobody knows how many rows there
 * are until the insured tells us.
 */
function TableField({ field, value, onChange, readOnly }: {
  field: FormField;
  value: FormAnswers[string];
  onChange: (v: FormAnswers[string]) => void;
  readOnly?: boolean;
}) {
  const cols = field.columns ?? [];
  const rows = (Array.isArray(value) ? value : []) as Record<string, unknown>[];
  const fixed = field.rowLabels ?? null;
  // A free-row table always shows one row to type into. With none, the agent
  // gets column headings and an "Add row" button — which reads as broken on a
  // question like "Deadbolts in units", where the grid is one row across five
  // location columns and there was never anything to add.
  const shown = fixed
    ? fixed.map((label, i) => ({ label, row: rows[i] ?? {} }))
    : (rows.length ? rows : [{}]).map((row, i) => ({ label: String(i + 1), row }));

  const setCell = (rowIndex: number, colId: string, v: string) => {
    const next = [...rows];
    while (next.length <= rowIndex) next.push({});
    next[rowIndex] = { ...next[rowIndex], [colId]: v };
    onChange(next as never);
  };

  return (
    <div className="sf-table-wrap">
      <table className="sf-table">
        <thead>
          <tr>
            <th />
            {cols.map((c) => (
              <th key={c.id}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {shown.map(({ label, row }, i) => (
            <tr key={i}>
              <th scope="row">{label}</th>
              {cols.map((c) => (
                <td key={c.id}>
                  <input
                    type={c.type === 'number' || c.type === 'currency' ? 'number' : 'text'}
                    value={(row[c.id] as string) ?? ''}
                    disabled={readOnly}
                    onChange={(e) => setCell(i, c.id, e.target.value)}
                    aria-label={`${label} — ${c.label}`}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {!fixed && !readOnly && (
        <button type="button" className="sf-addrow" onClick={() => onChange([...rows, {}] as never)}>
          + Add row
        </button>
      )}
    </div>
  );
}

export function SupplementalForm({ form, answers, onChange, audience, readOnly }: Props) {
  const visible = useMemo(
    () => form.fields.filter((f) => isVisible(f, answers, audience)),
    [form.fields, answers, audience],
  );
  const progress = completeness(form, answers, audience);

  // Only render a section that still has questions after filtering — otherwise
  // the insured scrolls past empty headings for the agent's half of the form.
  const sections = form.sections.filter((s) => visible.some((f) => f.section === s.id));

  return (
    <div className="sf">
      <div className="sf-progress" role="status">
        <div className="sf-bar">
          <span style={{ width: `${progress.pct}%` }} />
        </div>
        <span className="sf-progress-text">
          {progress.answered} of {progress.total} answered
        </span>
      </div>

      {sections.map((section) => (
        <section key={section.id} className="sf-section">
          <h3>{section.title}</h3>
          {section.intro && <p className="sf-intro">{section.intro}</p>}

          <div className="sf-fields">
            {visible
              .filter((f) => f.section === section.id)
              .map((field) => (
                <div key={field.id} className={`sf-field is-${field.type}${takesFullRow(field) ? ' is-wide' : ''}`}>
                  <label htmlFor={field.id}>
                    {field.label}
                    {field.required && <span className="sf-req" aria-hidden="true"> *</span>}
                  </label>
                  {field.help && (
                    <div className="sf-help" id={`${field.id}-help`}>
                      {field.help}
                    </div>
                  )}
                  {field.type === 'table' ? (
                    <TableField field={field} value={answers[field.id] ?? null} onChange={(v) => onChange(field.id, v)} readOnly={readOnly} />
                  ) : (
                    <Scalar field={field} value={answers[field.id] ?? null} onChange={(v) => onChange(field.id, v)} readOnly={readOnly} />
                  )}
                </div>
              ))}
          </div>
        </section>
      ))}

      {!sections.length && (
        <p className="sf-empty">
          Nothing here needs your answer — everything on this form is for the other party.
        </p>
      )}
    </div>
  );
}
